/**
 * Columns of the task list, with widths the user can drag.
 *
 * Widths live in one place because the list and the chart have to agree on
 * where the list ends: in the side-by-side view the sum of these widths IS the
 * width of the left pane. Storing them per browser means a long project name
 * stays readable once, not once per session.
 */
export type ColumnKey =
  | 'gutter'
  | 'code'
  | 'name'
  | 'duration'
  | 'start'
  | 'finish'
  | 'float'
  | 'variance'
  | 'predecessors'
  | 'status'
  | 'owner'
  | 'position'
  | 'deadline';

export interface ColumnSpec {
  key: ColumnKey;
  /** Heading. Units belong here, not repeated in every cell. */
  label: string;
  /** Tooltip on the heading, for what an abbreviation stands for. */
  title?: string;
  min: number;
  /** Numeric columns are right-aligned, so digits line up. */
  align?: 'right';
  /** Not resizable - the row controls have a fixed size. */
  fixed?: boolean;
  /** Shown only when switched on in the column menu. */
  optional?: boolean;
}

/**
 * Order follows how a schedule is read: what, how long, when, how much room,
 * how far off plan, and what it waits for. The row controls sit on the far
 * left, where the eye starts a row and where a destructive action is least
 * likely to be hit while reaching for a date.
 */
export const COLUMNS: readonly ColumnSpec[] = [
  { key: 'gutter', label: '', min: 46, fixed: true },
  { key: 'code', label: 'Nr.', title: 'Gliederungsnummer', min: 52 },
  { key: 'name', label: 'Vorgang', min: 160 },
  { key: 'duration', label: 'Dauer (AT)', title: 'Dauer in Arbeitstagen', min: 64, align: 'right' },
  { key: 'start', label: 'Anfang', min: 96 },
  { key: 'finish', label: 'Ende', min: 96 },
  { key: 'float', label: 'Puffer (AT)', title: 'Gesamtpuffer in Arbeitstagen', min: 66, align: 'right' },
  { key: 'variance', label: 'Abw. (AT)', title: 'Abweichung des Endes vom Basisplan, in Arbeitstagen', min: 66, align: 'right' },
  { key: 'predecessors', label: 'Vorgänger', title: 'Anordnungsbeziehungen nach DIN 69900', min: 80 },
  { key: 'status', label: 'Status', min: 76, optional: true },
  { key: 'owner', label: 'Verantw.', title: 'Verantwortlich', min: 60, optional: true },
  { key: 'position', label: 'Pos.', title: 'Angebotsposition oder Kategorie', min: 40, optional: true },
  { key: 'deadline', label: 'Spätestens', title: 'Spätestes Ende. Verschiebt den Plan nicht; ein gerechnetes Ende danach steht rot.', min: 96, optional: true },
];

export type ColumnWidths = Record<ColumnKey, number>;

/** Generous default for the name, which is what people actually read. */
export const DEFAULT_WIDTHS: ColumnWidths = {
  gutter: 46,
  code: 60,
  name: 320,
  duration: 72,
  start: 100,
  finish: 100,
  float: 76,
  variance: 76,
  predecessors: 110,
  status: 90,
  owner: 80,
  position: 50,
  deadline: 100,
};

/**
 * What a narrow screen shows. On a phone the list is for finding a task and
 * seeing when it happens; float and variance are for the desk.
 */
export const MOBILE_COLUMNS: ReadonlySet<ColumnKey> = new Set([
  'gutter',
  'code',
  'name',
  'start',
  'finish',
]);

/** Optional columns shown until the user decides otherwise. */
export const DEFAULT_OPTIONAL: ReadonlySet<ColumnKey> = new Set(['status', 'owner']);

/** The columns to show right now. */
export function visibleColumns(options: {
  mobile: boolean;
  hasBaseline: boolean;
  optional?: ReadonlySet<ColumnKey>;
}): readonly ColumnSpec[] {
  const optional = options.optional ?? DEFAULT_OPTIONAL;
  return COLUMNS.filter((column) => {
    if (options.mobile && !MOBILE_COLUMNS.has(column.key)) return false;
    if (column.optional && !optional.has(column.key)) return false;
    // A variance column without a baseline would be a column of dashes.
    if (column.key === 'variance' && !options.hasBaseline) return false;
    return true;
  });
}

const STORAGE_KEY = 'pst.schedule.columns';

export function loadWidths(): ColumnWidths {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_WIDTHS };
    const parsed = JSON.parse(raw) as Partial<ColumnWidths>;
    const merged = { ...DEFAULT_WIDTHS };
    for (const column of COLUMNS) {
      const value = parsed[column.key];
      if (typeof value === 'number' && value >= column.min) {
        merged[column.key] = value;
      }
    }
    return merged;
  } catch {
    // A browser with storage disabled is not a reason to fail; the defaults
    // are perfectly usable.
    return { ...DEFAULT_WIDTHS };
  }
}

export function saveWidths(widths: ColumnWidths): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(widths));
  } catch {
    // Ignored for the same reason.
  }
}

export function totalWidth(
  widths: ColumnWidths,
  columns: readonly ColumnSpec[] = COLUMNS,
): number {
  return columns.reduce((sum, column) => sum + widths[column.key], 0);
}

/**
 * Spare width goes to the name column.
 *
 * Without this the list would end wherever the columns happen to add up and
 * leave bare background beside it - which is exactly what it looked like in the
 * stacked arrangement.
 */
export function stretchToFit(
  widths: ColumnWidths,
  available: number,
  columns: readonly ColumnSpec[] = COLUMNS,
): ColumnWidths {
  const spare = available - totalWidth(widths, columns);
  if (spare <= 0) return widths;
  return { ...widths, name: widths.name + spare };
}

export type Orientation = 'vertical' | 'horizontal';

const SPLIT_KEY = 'pst.schedule.split';

/** Position of the divider: pane width in pixels, or height when stacked. */
export function loadSplit(orientation: Orientation, fallback: number): number {
  try {
    const raw = window.localStorage.getItem(`${SPLIT_KEY}.${orientation}`);
    const value = raw === null ? Number.NaN : Number(raw);
    return Number.isFinite(value) && value > 80 ? value : fallback;
  } catch {
    return fallback;
  }
}

export function saveSplit(orientation: Orientation, value: number): void {
  try {
    window.localStorage.setItem(`${SPLIT_KEY}.${orientation}`, String(value));
  } catch {
    // A browser with storage disabled keeps the default; not worth failing for.
  }
}

const ORIENTATION_KEY = 'pst.schedule.orientation';

export function loadOrientation(): Orientation {
  try {
    return window.localStorage.getItem(ORIENTATION_KEY) === 'horizontal'
      ? 'horizontal'
      : 'vertical';
  } catch {
    return 'vertical';
  }
}

export function saveOrientation(orientation: Orientation): void {
  try {
    window.localStorage.setItem(ORIENTATION_KEY, orientation);
  } catch {
    // Ignored.
  }
}

const OPTIONAL_KEY = 'pst.schedule.optionalColumns';

export function loadOptionalColumns(): Set<ColumnKey> {
  try {
    const raw = window.localStorage.getItem(OPTIONAL_KEY);
    if (!raw) return new Set(DEFAULT_OPTIONAL);
    const keys = JSON.parse(raw) as string[];
    return new Set(COLUMNS.filter((c) => c.optional && keys.includes(c.key)).map((c) => c.key));
  } catch {
    return new Set(DEFAULT_OPTIONAL);
  }
}

export function saveOptionalColumns(keys: ReadonlySet<ColumnKey>): void {
  try {
    window.localStorage.setItem(OPTIONAL_KEY, JSON.stringify([...keys]));
  } catch {
    // Storage disabled: the choice lasts for this page view.
  }
}
